import React from 'react';
import { parseUrl } from 'query-string';
import { hex } from 'wcag-contrast';

import VisionDefectColor from '../lib/VisionDefectColor';


const DEFECT_MAP = {
    n: 'normalVision',
    pr: 'protanopeVision',
    de: 'deuteranopeVision',
    tr: 'tritanopeVision',
    cb: 'colorBlindnessVision',
};
const DEFECT_MAP_KEYS = Object.keys(DEFECT_MAP);

const visionDefectColor = new VisionDefectColor();

function createColorDeficiencyMap(hex) {
    const hexadecimal = Number(hex.replace('#', '0x'));
    return DEFECT_MAP_KEYS.reduce((obj, key) => {
        obj[key] = visionDefectColor
            .convertDefect(DEFECT_MAP[key], hexadecimal)
            .replace('0x', '#');
        return obj;
    }, {});
}

const ColorContrastStateContext = React.createContext();

function ColorContrastProvider({ children }) {
    const searchParams = parseUrl(window.location.href).query;

    const [fgColor, setFgColor] = React.useState(
        searchParams.fgColor || '#252728'
    );
    const [bgColor, setBgColor] = React.useState(
        searchParams.bgColor || '#d1d6e7'
    );

    const value = React.useMemo(
        () => [fgColor, setFgColor, bgColor, setBgColor],
        [fgColor, bgColor]
    );

    return (
        <ColorContrastStateContext.Provider value={value}>
            {children}
        </ColorContrastStateContext.Provider>
    );
}

function useColorContrastState() {
    const context = React.useContext(ColorContrastStateContext);

    if (context === undefined) {
        throw new Error(
            'useColorContrastState must be used within a ColorContrastProvider'
        );
    }
    const [fgColor, setFgColor, bgColor, setBgColor] = context;

    // Generate color deficiency maps for each color deficiency
    const fgColorDeficiencyMap = createColorDeficiencyMap(fgColor);
    const bgColorDeficiencyMap = createColorDeficiencyMap(bgColor);

    // Calculate contrast between fg and bg colors for each deficiency
    const contrastRatio = {
        // Normal
        n: hex(fgColor, bgColor),
        // Protanope
        pr: hex(fgColorDeficiencyMap.pr, bgColorDeficiencyMap.pr),
        // Deuteranope
        de: hex(fgColorDeficiencyMap.de, bgColorDeficiencyMap.de),
        // Tritanope
        tr: hex(fgColorDeficiencyMap.tr, bgColorDeficiencyMap.tr),
        // Color Blindness
        cb: hex(fgColorDeficiencyMap.cb, bgColorDeficiencyMap.cb),
    };

    const swapColors = () => {
        setFgColor(bgColor);
        setBgColor(fgColor);
    };

    return {
        fgColor: fgColor.toUpperCase(),
        setFgColor,
        bgColor: bgColor.toUpperCase(),
        setBgColor,
        swapColors,
        fgColorDeficiencyMap,
        bgColorDeficiencyMap,
        contrastRatio,
    };
}

export { ColorContrastProvider, useColorContrastState };
